import "common/Charts/chartRegistry";
import PieChartExtraInfoGrid from "common/Charts/PieChart/PieChartExtraInfoGrid";
import styled, { useTheme } from "styled-components";
import { Doughnut } from "react-chartjs-2";
import Title from "common/Typography/Title";
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import useMeasure from "utils/hooks/useMeasure";
import {
  getTooltipGenerator,
  pieChartTooltipInnerHtmlGenerator,
} from "common/Charts/tooltipGenerator.jsx";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import Flex from "common/Boxes/Flex";
import { DEFAULT_CHART_TOOLTIP_MARGIN } from "common/constants";
import { useBottomNavigator } from "utils/hooks/misc";
import { LanguageNameSpaces, PieChartDataPoint } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

interface PieChartProps {
  data: PieChartDataPoint[];
  loading?: boolean;
  chartName?: ReactNode;
  total?: ReactNode;
  numGridItems?: number;
  numItemsInRow?: number;
  numRows?: number;
  chartParentElementId?: string;
  tooltipElementId?: string;
  horizontal?: boolean;
  chartSize?: number;
  halfChart?: boolean;
  chartWeight?: string;
  chartStyle?: CSSProperties;
  wrapperStyle?: CSSProperties;
  extraInfoStyle?: CSSProperties;
  extraInfoOnTheLeft?: boolean;
  hideTotal?: boolean;
  gap?: [number, number];
  tooltipMargin?: number;
  hideInnerCircle?: boolean;
  hideTooltip?: boolean;
  plugin?: any;
  borderWidth?: number;
  disableHover?: boolean;
  isMobileMainPageChart?: boolean;
  isTwoLinesEllipsis?: boolean;
}

const PieChart = ({
  data,
  loading = false,
  chartName,
  total,
  numGridItems,
  numItemsInRow = 0,
  numRows = 0,
  chartParentElementId = "pieChart",
  tooltipElementId = "pie_chart",
  horizontal = false,
  chartSize: propsChartSize,
  halfChart = false,
  chartWeight = CHART_WEIGHT,
  chartStyle = {},
  wrapperStyle = {},
  extraInfoStyle = {},
  extraInfoOnTheLeft = false,
  hideTotal = false,
  gap,
  tooltipMargin = DEFAULT_CHART_TOOLTIP_MARGIN,
  hideInnerCircle = false,
  hideTooltip = false,
  plugin = undefined,
  borderWidth = 5,
  disableHover = false,
  isMobileMainPageChart,
  isTwoLinesEllipsis,
}: PieChartProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const appTheme = useTheme();
  const [measureInnerCircle, { width: innerCircleWidth }] =
    useMeasure<HTMLDivElement>();
  const [measureTotalText, { width: totalTextWidth }] =
    useMeasure<HTMLHeadingElement>();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const [titleLevel, setTitleLevel] = useState(isBottomNavigator ? 2 : 1);
  const chartSize = isMobileMainPageChart ? 170 : propsChartSize ?? CHART_SIZE;

  const chartData = {
    datasets: [
      {
        data: data.map((dataPoint) => dataPoint.value),
        backgroundColor: data.map((dataPoint) => dataPoint.color),
        borderWidth: borderWidth,
        borderColor: appTheme.colors.cardBgColor,
        rawData: data,
      },
    ],
  };

  const tooltipGenerator = getTooltipGenerator(
    tooltipElementId,
    pieChartTooltipInnerHtmlGenerator,
    false,
    tooltipMargin,
    chartParentElementId
  );

  let options: any = {
    cutout: isMobileMainPageChart ? "88%" : chartWeight,
    maintainAspectRatio: false,
    responsive: true,
    animation: !isBottomNavigator,
    plugins: {
      tooltip: {
        enabled: false,
        external: hideTooltip ? undefined : tooltipGenerator,
      },
      legend: {
        display: false,
      },
    },
  };

  if (halfChart) {
    options = { ...options, rotation: -90, circumference: 180 };
  }
  if (disableHover) {
    options.hover = { mode: null };
  }

  const chart = useMemo(() => {
    return (
      <Doughnut
        data={chartData}
        options={options}
        plugins={plugin ? [plugin] : undefined}
      />
    );
  }, [data, displayCurrency, plugin, appTheme]);

  useEffect(() => {
    if (totalTextWidth > innerCircleWidth && titleLevel < 5) {
      setTitleLevel(titleLevel + 1);
    }
  }, [totalTextWidth, innerCircleWidth]);

  const getLegend = () => {
    if (isMobileMainPageChart) {
      return (
        <PieChartExtraInfoGrid
          data={data}
          gap={gap}
          isMobileMainPageChart={true}
          isTwoLinesEllipsis={isTwoLinesEllipsis}
        />
      );
    }
    return (
      <div
        style={{
          marginTop: horizontal ? 0 : 14,
          marginLeft: horizontal ? 14 : 0,
          ...extraInfoStyle,
        }}
      >
        <PieChartExtraInfoGrid
          data={data}
          numGridItems={numGridItems}
          numItemsInRow={numItemsInRow}
          numRows={numRows}
          horizontal={horizontal}
          gap={gap}
          isTwoLinesEllipsis={isTwoLinesEllipsis}
        />
      </div>
    );
  };

  const style: CSSProperties = {
    width: chartSize,
    height: halfChart ? chartSize / 2 + 10 : chartSize,
    position: "relative",
    margin: "auto",
  };

  let innerWrapperStyle = chartStyle;
  if (!halfChart)
    innerWrapperStyle = {
      flex: "1 1 auto",
      ...style,
      ...innerWrapperStyle,
    };

  const isHorizontal = horizontal || isBottomNavigator;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        margin: isBottomNavigator ? "12px 0" : undefined,
        ...wrapperStyle,
      }}
    >
      <LoadingSpinnerOverlay
        isLoading={loading}
        backgroundColor={
          appTheme.colors[
            isMobileMainPageChart ? "layoutBgColor" : "cardBgColor"
          ]
        }
      >
        {!isMobileMainPageChart && !!chartName && (
          <Title type="secondary" level={3} style={{ marginBottom: 12 }}>
            {chartName}
          </Title>
        )}
        <Flex
          $flexDirection={isHorizontal ? "row" : "column"}
          style={{ flex: 1 }}
          $alignItems={halfChart ? "flex-end" : "center"}
        >
          {extraInfoOnTheLeft && getLegend()}
          <div style={innerWrapperStyle} id={chartParentElementId}>
            {halfChart ? <div style={style}>{chart}</div> : chart}
            {!hideInnerCircle && (
              <InnerCircle
                ref={measureInnerCircle}
                $chartSize={chartSize}
                $halfChart={halfChart}
              />
            )}
            {!hideTotal && (
              <InnerChartTextWrapper>
                <Title
                  type="primary"
                  level={titleLevel}
                  innerRef={measureTotalText}
                >
                  {total}
                </Title>
                <Title level={3}>{t("labels.total")}</Title>
              </InnerChartTextWrapper>
            )}
          </div>
          {!extraInfoOnTheLeft && getLegend()}
        </Flex>
      </LoadingSpinnerOverlay>
    </div>
  );
};

export default React.memo(PieChart);

const CHART_SIZE = 240;
const CHART_WEIGHT = "90%";

const getSize = (chartSize: number, halfChart: boolean, isHeight?: boolean) =>
  (chartSize - (halfChart || chartSize < 200 ? 30 : 40)) /
  (isHeight && halfChart ? 2 : 1);

const InnerCircle = styled.div<{ $chartSize: number; $halfChart: boolean }>`
  position: absolute;
  top: ${(p) => (p.$halfChart ? 62 : 50)}%;
  left: 50%;
  transform: ${(p) =>
    p.$halfChart ? "translate(-50%, -55%)" : "translate(-50%, -50%)"};
  height: ${(p) => getSize(p.$chartSize, p.$halfChart, true)}px;
  width: ${(p) => getSize(p.$chartSize, p.$halfChart)}px;
  border-radius: ${(p) => (p.$halfChart ? "150px 150px 0 0" : "50%")};
  border: 1px solid ${(p) => p.theme.colors.pieChartBorderColor};
  ${(p) =>
    p.$halfChart &&
    `
    border-bottom: 0;
    `};
`;
const InnerChartTextWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;
